import { Organization } from "../../../gen/witbooking/loyalty/organization/v1/organization_api_pb";

export type OrgNode = Organization.AsObject & { children: OrgNode[] };

// Cycle-safe: a parentId that doesn't resolve to a known node, or that would create a loop,
// leaves the node at the top level instead of being silently dropped.
export function buildOrganizationTree(entities: Organization.AsObject[]): OrgNode[] {
  const byId = new Map<string, OrgNode>();
  entities.forEach(e => byId.set(e.id, { ...e, children: [] }));

  // Walking UP the parent chain from startId, do we ever reach targetId? (i.e. is startId a
  // descendant of targetId)
  const isDescendantOf = (startId: string, targetId: string): boolean => {
    let current = byId.get(startId);
    const seen = new Set<string>();
    while (current) {
      if (current.id === targetId) return true;
      if (seen.has(current.id)) return false;
      seen.add(current.id);
      current = current.parentId ? byId.get(current.parentId) : undefined;
    }
    return false;
  };

  const roots: OrgNode[] = [];
  byId.forEach(node => {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    // Reject attaching node under parent when parent is already a descendant of node — that
    // would close a cycle (e.g. a -> b -> a).
    if (parent && parent.id !== node.id && !isDescendantOf(parent.id, node.id)) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

export function flattenOrganizationTree(
  nodes: OrgNode[],
  depth = 0
): Array<{ node: OrgNode; depth: number }> {
  return nodes.flatMap(node => [
    { node, depth },
    ...flattenOrganizationTree(node.children, depth + 1),
  ]);
}
